/**
 * The boat landing below the west quay (lifted from lantern-water's
 * `boat-landing.js`): four broad limestone steps from the quay down to a
 * narrow timber landing beside the moored skiff (each step's cap owns its
 * tread plane, the body stops beneath it; the first cap is trimmed at the
 * quay's edge x 4, and the planks are cut around the last step's footprint so
 * no two surfaces share a plane), six mooring piles with iron caps, an iron
 * hand rail with three posts down the steps, and rope rails at the closed end.
 * Placed at the origin (its points are the town's, by the canal's west quay).
 * No params. Its exclusive walking surfaces (the stepped flight and the deck
 * at -0.4), the deck's openings in the canal, the rope's collider and the
 * mooring notice are the scene record's.
 */
import * as THREE from 'three';
import type { Prefab } from './types.ts';

export const prefab: Prefab = {
  id: 'boat_landing',
  buildPrefab(ctx) {
    const root = new THREE.Group();
    root.name = 'boat-landing';
    const { box, cylinder, line, m } = ctx;
    const capThickness = 0.07;
    const bodyHeight = 0.28 - capThickness;
    const treadWidth = 0.38;
    const stairDepth = 1.62;
    const stairEnd = 4.025 + 3 * 0.35 + treadWidth / 2;
    const stairSouth = 3 + stairDepth / 2;
    for (let i = 0; i < 4; i += 1) {
      const x = 4.025 + i * 0.35;
      const top = 0.02 - i * 0.14;
      box(root, x, top - capThickness - bodyHeight / 2, 3, 0.35, bodyHeight, 1.55, m.stone);
      const left = i === 0 ? 4 : x - treadWidth / 2;
      const right = x + treadWidth / 2;
      box(root, (left + right) / 2, top - capThickness / 2, 3, right - left, capThickness, stairDepth, m.cream);
    }
    // Planks run across a narrow landing beside the moored boat, cut around the stone footprint.
    for (let i = 0; i < 24; i += 1) {
      const z = 2.65 + i * 0.177;
      const near = z - 0.083;
      const far = z + 0.083;
      for (const [lo, hi, left] of [
        [near, Math.min(far, stairSouth), stairEnd],
        [Math.max(near, stairSouth), far, 4.7],
      ] as const) {
        if (hi <= lo) continue;
        box(root, (left + 5.9) / 2, -0.455, (lo + hi) / 2, 5.9 - left, 0.11, hi - lo, m.wood);
      }
    }
    for (const x of [4.75, 5.85]) {
      for (const z of [2.62, 4.6, 6.7]) {
        cylinder(root, x, -0.67, z, 0.085, 1.2, m.darkWood);
        cylinder(root, x, -0.05, z, 0.11, 0.065, m.iron);
      }
    }
    line(root, [3.7, 0.83, 2.12], [5.45, 0.27, 2.12], 0.05, m.iron);
    for (const x of [3.8, 4.5, 5.2]) {
      const top = 0.02 - Math.max(0, Math.min(3, Math.floor((x - 3.85) / 0.35))) * 0.14;
      cylinder(root, x, top + 0.38, 2.12, 0.035, 0.76, m.iron);
    }
    // Rope rails mark the closed end; the stair entrance and boat side stay clear.
    line(root, [4.75, -0.05, 6.8], [5.85, -0.05, 6.8], 0.028, m.canvas);
    for (const z of [5, 6.4]) line(root, [5.86, -0.08, z], [6.04, -0.35, z + 0.16], 0.018, m.canvas);
    return { root };
  },
};
